import React, { useState, useEffect } from 'react';
import formStyles from '../../styles/SmartSendForm.module.css';
import { getResendCooldownRemaining } from '../../utils/localStorage';

/**
 * PhoneVerificationStep - Step 1: Phone number input and verification
 * Allows user to enter phone number and request SMS verification code
 * @param {string} phoneNumber - Current phone number value
 * @param {Function} onPhoneChange - Handler for phone input changes
 * @param {Function} onConfirm - Handler for sending verification code
 * @param {boolean} isLoading - Loading state during code sending
 * @param {string} orderId - SmartSend order identifier for scoping cooldown
 */
const PhoneVerificationStep = ({
  phoneNumber,
  onPhoneChange,
  onConfirm,
  isLoading = false,
  orderId,
}) => {
  const [cooldownSeconds, setCooldownSeconds] = useState(null);

  useEffect(() => {
    // Check initial cooldown state
    if (!orderId) return;
    const remaining = getResendCooldownRemaining(orderId);
    if (remaining !== null) {
      setCooldownSeconds(remaining);
    }
  }, [orderId]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds === null || cooldownSeconds <= 0 || !orderId) {
      return;
    }

    const timer = setInterval(() => {
      const remaining = getResendCooldownRemaining(orderId);
      if (remaining === null || remaining <= 0) {
        setCooldownSeconds(null);
      } else {
        setCooldownSeconds(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds, orderId]);

  /**
   * Format cooldown time as MM:SS
   * @param {number} seconds - Seconds remaining
   * @returns {string} Formatted time string
   */
  const formatCooldownTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isButtonDisabled = !phoneNumber.trim() || isLoading || cooldownSeconds !== null;

  return (
    <div
      className={formStyles.verificationCard}
      role="form"
      aria-label="Phone verification"
    >
      <label className={formStyles.label} htmlFor="phone">
        Phone number
      </label>
      <input
        id="phone"
        type="tel"
        inputMode="tel"
        placeholder="(555) 123-4567"
        className={formStyles.input}
        value={phoneNumber}
        onChange={(e) => onPhoneChange(e.target.value)}
        disabled={isLoading}
        aria-label="Enter your phone number"
        aria-required="true"
        aria-invalid={!phoneNumber.trim() && phoneNumber.length > 0}
        aria-describedby="phone-help"
      />
      <span id="phone-help" className="sr-only">
        Enter your phone number to receive a verification code
      </span>

      <button
        className={formStyles.button}
        onClick={onConfirm}
        disabled={isButtonDisabled}
        aria-label={
          isLoading
            ? 'Sending verification code'
            : cooldownSeconds !== null
            ? `Please wait ${formatCooldownTime(cooldownSeconds)} before requesting another code`
            : 'Confirm phone number and send code'
        }
        aria-busy={isLoading}
      >
        {isLoading
          ? 'Sending code...'
          : cooldownSeconds !== null
          ? `Wait ${formatCooldownTime(cooldownSeconds)}`
          : 'Confirm'}
      </button>

      {/* Cooldown notice */}
      {cooldownSeconds !== null && !isLoading && (
        <p className={formStyles.cooldownNotice} role="status" aria-live="polite">
          A code was recently sent. Please wait before requesting another.
        </p>
      )}

      {/* Terms & Policy - Only show when button is clickable */}
      {cooldownSeconds === null && (
        <p className={formStyles.policyText}>
          By clicking "Confirm" I represent that I have read and agree to the{' '}
          <a
            href="https://www.gesture.vip/legal/m-terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            className={formStyles.policyLink}
            aria-label="Open Gesture Terms of Service in new tab"
          >
            Gesture Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://www.gesture.vip/legal/m-privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className={formStyles.policyLink}
            aria-label="Open Privacy Policy in new tab"
          >
            Privacy Policy
          </a>
          .
        </p>
      )}
    </div>
  );
};

export default PhoneVerificationStep;
